/**
 * UnitCard — Step 2 of the Math Engine wizard.
 *
 * Single grade: one Focus line, that grade's day count.
 * Split-grade pair: both grades' day counts and both Focus lines (labelled,
 * verbatim from each grade's scope file). An amber "Runs divided" badge shows
 * only for Tier 1 units; Tier 2 and positive notes carry no card badge.
 */
import React from "react";

const UnitCard = ({ unit, selected, onSelect }) => {
  const isPair = !!unit.is_pair;
  const miniCount = isPair
    ? (unit.mini_unit_pairs || []).length
    : (unit.mini_units || []).length;
  const meta = unit.pair_grade_meta || [];
  const notice = isPair ? unit.notice : null;
  const tier = notice ? notice.tier : null;
  const BADGE = {
    runs_divided: {
      text: "Some lessons run separately",
      cls: "bg-amber-100 text-amber-800",
    },
    plan_two: {
      text: "Some lessons need two tasks",
      cls: "bg-[#4a6984]/12 text-[#1c2d3d]",
    },
  };
  const badge = tier && BADGE[tier];

  return (
    <button
      type="button"
      onClick={() => onSelect(unit)}
      data-testid={`math-engine-unit-${unit.unit_number}`}
      className={`rounded-xl border-2 p-5 text-left transition-all ${
        selected
          ? "border-[#96ded1] bg-[#96ded1]/10 shadow-sm"
          : "border-[#4a6984]/15 bg-white hover:border-[#4a6984]/30 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          {isPair && meta.length === 2
            ? `Unit ${unit.unit_number} · ${meta[0].grade} ${meta[0].day_budget} · ${meta[1].grade} ${meta[1].day_budget}`
            : `Unit ${unit.unit_number} · ${unit.day_budget}`}
        </p>
        {miniCount > 0 ? (
          <span className="shrink-0 text-[10px] font-bold text-[#1c2d3d] bg-[#96ded1]/30 px-2 py-0.5 rounded-full">
            {miniCount} mini-units
          </span>
        ) : null}
      </div>

      <p className="font-extrabold text-lg text-[#1c2d3d] tracking-tight leading-tight">
        {unit.name}
      </p>

      {badge ? (
        <span
          data-testid={`math-engine-unit-badge-${unit.unit_number}`}
          className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-tight ${badge.cls}`}
        >
          {badge.text}
        </span>
      ) : null}

      {isPair && meta.length === 2 ? (
        <div className="mt-2 space-y-1.5">
          {meta.map((m) => (
            <p key={m.grade} className="text-xs text-[#4a6984]/85 leading-snug">
              <span className="font-bold text-[#1c2d3d]">{m.grade}:</span>{" "}
              {m.focus.split("\n")[0]}
            </p>
          ))}
        </div>
      ) : (
        unit.focus && (
          <p className="mt-2 text-xs text-[#4a6984]/85 leading-snug line-clamp-3">
            {unit.focus.split("\n")[0]}
          </p>
        )
      )}

      {tier === "positive" && notice ? (
        <div
          data-testid={`math-engine-unit-positive-${unit.unit_number}`}
          className="mt-2 rounded-lg border border-[#96ded1]/60 bg-[#96ded1]/20 px-3 py-2 text-xs text-[#12463b] leading-snug"
        >
          <span className="font-bold">{notice.title}</span>{" "}
          {(notice.body || []).join(" ")}
        </div>
      ) : null}
    </button>
  );
};

export default UnitCard;
